import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { EffectState } from '@/pixi/filters/types'

interface KaleidoscopeUniforms {
  isAudioReactive: boolean,
  audioIntensity: number,
  mirrorOffset: number,
  numberOfMirrors: number
}

interface KaleidoscopeParams {
  numberOfMirrors: number
}

export class KaleidoscopeFilter extends BaseSeratoFilter {

  get uniforms (): KaleidoscopeUniforms {
    return super.uniforms as KaleidoscopeUniforms
  }

  constructor (effectState: EffectState, params: KaleidoscopeParams) {
    const shader = `
    // constants
    #define PI 3.1415926538

    // generic default params
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;

    // varying params to tune effects
    uniform float mirrorOffset;
    uniform float audioIntensity;
    uniform bool isAudioReactive;
    uniform float numberOfMirrors;


    vec2 rotateUV(vec2 uv, float rotation) {
      vec2 pivot = vec2(0.5, 0.5);

      float cosa = cos(rotation);
      float sina = sin(rotation);
      uv -= pivot;
      return vec2(cosa * uv.x - sina * uv.y, cosa * uv.y + sina * uv.x) + pivot;
    }

    vec2 addOffsetAndAudioReactivity(vec2 uv) {
      // sqrt audioIntensity to lessen it's effect
      return uv / (mirrorOffset * (isAudioReactive ? sqrt(audioIntensity + 1.0) : 1.01));
    }

    void main(void)
    {
      vec2 vtc = vTextureCoord; // shorter name
      //Calculate the radians of rotation from the number of mirrors
      float angle = PI/(numberOfMirrors * 2.0);
      float reverseAngle = -3.0 * angle;

      // create mirrored segments
      vec2 u0 = vtc;
      vec2 u1 = vec2(1.0 - u0.x, u0.y);
      vec2 u2 = vec2(u0.x, 1.0 - u0.y);
      vec2 u3 = vec2(1.0 - u0.x, 1.0 - u0.y);

      // rotate these segments to create 8 wedges
      vec2 uv0 = addOffsetAndAudioReactivity(rotateUV(u0, angle));
      vec2 uv1 = addOffsetAndAudioReactivity(rotateUV(u1, angle));
      vec2 uv2 = addOffsetAndAudioReactivity(rotateUV(u2, angle));
      vec2 uv3 = addOffsetAndAudioReactivity(rotateUV(u3, angle));
      vec2 uv0m = addOffsetAndAudioReactivity(rotateUV(u0, reverseAngle));
      vec2 uv2m = addOffsetAndAudioReactivity(rotateUV(u1, reverseAngle));
      vec2 uv1m = addOffsetAndAudioReactivity(rotateUV(u2, reverseAngle));
      vec2 uv3m = addOffsetAndAudioReactivity(rotateUV(u3, reverseAngle));

      // textures from wedges
      vec4 tex0 = texture2D(uSampler, uv0.xy);
      vec4 tex1 = texture2D(uSampler, uv1.xy);
      vec4 tex2 = texture2D(uSampler, uv2.xy);
      vec4 tex3 = texture2D(uSampler, uv3.xy);
      vec4 tex0m = texture2D(uSampler, uv0m.xy);
      vec4 tex1m = texture2D(uSampler, uv1m.xy);
      vec4 tex2m = texture2D(uSampler, uv2m.xy);
      vec4 tex3m = texture2D(uSampler, uv3m.xy);

      vec4 texColor = vec4(0.0, 0.0, 0.0, 0.0);
      // Create vector from center of canvas to vtc
      vec2 normVtc = vtc - vec2(0.5, 0.5);

      // Create diagonal mirror vectors
      vec2 m1 = vec2(cos(2.0 * angle), sin(2.0 * angle));
      vec2 m2 = vec2(cos(-2.0 * angle), sin(-2.0 * angle));
      
      // use dot product to determine which side of mirror lines the vtx is and then assign to wedges accordingly
      if (numberOfMirrors == 4.0) {
        if (vtc.x >= 0.5 && vtc.y >= 0.5) {
            texColor = acos(dot(normVtc, m2)) >= PI/2.0 ? tex0 : tex2m;
        } else if (vtc.x <= 0.5 && vtc.y >= 0.5) {
            texColor = acos(dot(normVtc, m1)) <= PI/2.0 ? tex1 : tex0m;
        } else if (vtc.x >= 0.5 && vtc.y <= 0.5) {
            texColor = acos(dot(normVtc, m1)) >= PI/2.0 ? tex2 : tex3m;
        } else {
            texColor = acos(dot(normVtc, m2)) <= PI/2.0 ? tex3 : tex1m;
        }
      } else {
        if (vtc.x >= 0.5 && vtc.y >= 0.5) {
          texColor = tex0;
        } else if (vtc.x <= 0.5 && vtc.y >= 0.5) {
            texColor = tex1;
        } else if (vtc.x >= 0.5 && vtc.y <= 0.5) {
            texColor = tex2;
        } else {
            texColor = tex3;
        }
      }
        
      gl_FragColor = texColor.a == 0.0 ? vec4(0.0) : texColor;
    }`

    const uniforms: KaleidoscopeUniforms = {
      isAudioReactive: false,
      audioIntensity: 1,
      mirrorOffset: 1,
      numberOfMirrors: params.numberOfMirrors
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )
  }

  updateUniforms () {
    this.uniforms.mirrorOffset = this.effectState.intensity * 2
    this.uniforms.audioIntensity = this.getAudioIntensityValue()
    this.uniforms.isAudioReactive = this.effectState.audioReactivityEnabled
  }
}
